import { useWindowVirtualizer } from "@tanstack/react-virtual"
import React, { useEffect, useMemo, useState } from "react"

import { HEADER_HEIGHT_VARIABLE_NAME } from "shared/constants/app-constants"
import { useResponsiveColumns } from "shared/lib/breakpoints/useResponsiveColumns"
import { useElementHeightStore } from "shared/lib/css/useElementHeightStore"
import { useEvent } from "shared/lib/useEvent"
import { useRovingTabIndexGrid } from "shared/lib/useRovingTabIndexGrid"
import { BUILDING_REFERENCE_COLUMNS_BY_BREAKPOINT } from "shared/types/Breakpoints"
import { OnFocusedItemChange } from "shared/types/RovingTabIndex"

import { BuildingReferenceGridProps } from "../BuildingReferenceGrid"
import "../styles/_building-reference-grid.scss"
import { BuildingReferenceGridCard } from "./BuildingReferenceGridCard"

export interface BuildingReferenceGridViewProps
  extends BuildingReferenceGridProps {}

export const BuildingReferenceGridView = React.memo(
  ({
    buildingReferenceObjs,
    emptyMessage,
    container,
    priorityProducts
  }: BuildingReferenceGridViewProps) => {
    const headerHeight = useElementHeightStore(HEADER_HEIGHT_VARIABLE_NAME)

    const [parentElement, setParentElement] = useState<HTMLDivElement | null>(
      null
    )
    const scrollMargin = parentElement?.offsetTop ?? 0

    const columnCount = useResponsiveColumns(container || parentElement, {
      columnsByBreakpoint: BUILDING_REFERENCE_COLUMNS_BY_BREAKPOINT
    })

    const rows = useMemo(() => {
      const result = []
      for (let i = 0; i < buildingReferenceObjs.length; i += columnCount) {
        result.push(buildingReferenceObjs.slice(i, i + columnCount))
      }
      return result
    }, [buildingReferenceObjs, columnCount])

    const rowVirtualizer = useWindowVirtualizer({
      count: rows.length,
      gap: 24,
      estimateSize: () => 440,
      scrollMargin,
      overscan: 2
    })

    useEffect(() => {
      rowVirtualizer.measure()
    }, [buildingReferenceObjs])

    const handleElementFocus: OnFocusedItemChange = useEvent(({ index }) => {
      const [targetOffset] = rowVirtualizer.getOffsetForIndex(
        Math.floor(index / columnCount)
      ) || [0]

      const currentOffset = rowVirtualizer.scrollOffset ?? 0
      if (targetOffset < currentOffset) {
        rowVirtualizer.scrollToOffset(targetOffset - headerHeight)
      } else {
        rowVirtualizer.scrollToOffset(targetOffset)
      }
    })

    const { containerProps, getElementProps } = useRovingTabIndexGrid<
      HTMLAnchorElement,
      HTMLDivElement
    >({
      elementCount: buildingReferenceObjs.length,
      columnsByBreakpoint: BUILDING_REFERENCE_COLUMNS_BY_BREAKPOINT,
      isRoundRobin: false,
      disableRovingTabIndex: true,
      onFocusedElementChange: handleElementFocus
    })

    if (buildingReferenceObjs.length === 0) {
      return (
        <div className="building-reference-grid building-reference-grid--empty">
          <div className="building-reference-grid__empty-message">
            {emptyMessage}
          </div>
        </div>
      )
    }

    return (
      <div
        className="building-reference-grid"
        ref={setParentElement}
        role="list"
        aria-live="polite"
      >
        <div
          className="building-reference-grid__virtual-container"
          style={{
            height: `${rowVirtualizer.getTotalSize()}px`
          }}
          {...containerProps}
        >
          {rowVirtualizer.getVirtualItems().map(virtualRow => (
            <div
              key={virtualRow.index}
              data-index={virtualRow.index}
              ref={rowVirtualizer.measureElement}
              className="building-reference-grid__virtual-row"
              style={{
                transform: `translateY(${virtualRow.start - scrollMargin}px)`
              }}
            >
              <div
                className="building-reference-grid__row"
                style={
                  {
                    "--column-count": columnCount
                  } as React.CSSProperties
                }
              >
                {rows[virtualRow.index].map((obj, index) => {
                  const { ref, ...rovingTabIndexElementProps } =
                    getElementProps(virtualRow.index * columnCount + index)
                  return (
                    <div
                      className="building-reference-grid__item"
                      role="listitem"
                      aria-setsize={buildingReferenceObjs.length}
                      aria-posinset={virtualRow.index * columnCount + index + 1}
                      key={obj.id()}
                    >
                      <BuildingReferenceGridCard
                        buildingReferenceObj={obj}
                        linkRef={ref}
                        priorityProducts={priorityProducts}
                        {...rovingTabIndexElementProps}
                      />
                    </div>
                  )
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }
)
